import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Subject, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';

import {
  DailyRecurrence,
  Frequency,
  ScheduleType,
  TimeFormats,
  TimeZones,
  Weekdays,
  WeeklyRecurrence,
} from './data';
import {
  TransformFrom,
  EDialogWidth,
} from 'projects/shared/src/lib/core/enums';
import {
  cloneData,
  removeUnnecesaryKeysFromObject,
} from 'projects/shared/src/lib/core/handlers';
import {
  RecipientUserInterface,
  OptionInterface,
  CommentaryItemInterface,
  ContactInterface,
  DialogConfigInterface,
  TaskPriorityEnum,
  TaskStatusEnumOld,
  TaskFormInterface,
  RecurrenceSettingInterface,
} from 'projects/shared/src/lib/core/interfaces';
import { TasksService } from 'projects/shared/src/lib/core/services/tasks.service';
import { ToastrAbstractionService } from 'projects/shared/src/lib/core/services/toastr-abstraction.service';
import { UserService } from 'projects/shared/src/lib/core/services/user.service';
import { ConfirmDialogComponent } from '../../../navbar/components/confirm-dialog/confirm-dialog.component';
import { ContactService } from 'projects/shared/src/lib/core/services/contact.service';
import { BusinessClockService } from 'projects/shared/src/lib/core/services/business-clock.service';

@Component({
  selector: 'app-create-task-dialog',
  templateUrl: './create-task-dialog.component.html',
  styleUrls: ['./create-task-dialog.component.scss'],
  standalone: false,
})
export class CreateTaskDialogComponent implements OnInit, OnDestroy {
  public taskForm!: FormGroup;
  public recurrenceForm!: FormGroup;
  public usersByTenantId!: RecipientUserInterface[];
  public schedulingType = ScheduleType;
  public frequency = Frequency;
  public weekDays = Weekdays;
  public timeZones: OptionInterface[] = [];
  public timeFormats = TimeFormats;
  public comments: CommentaryItemInterface[] = [];
  public mode!: 'open' | 'edit';
  public action!: string;
  public allStatuses: any[] = [];
  public todayDate!: Date;
  public defaultTextRecurrenceType = ' week(s) on:';
  public textOfRecurrenceType = this.defaultTextRecurrenceType;

  public readonly separatorKeysCodes = [ENTER, COMMA] as const;
  private _currentUser!: ContactInterface | null;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CreateTaskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogConfigInterface,
    public tasksService: TasksService,
    private _userService: UserService,
    private _dialog: MatDialog,
    private _translate: TranslateService,
    private _fb: FormBuilder,
    private _toastr: ToastrAbstractionService,
    private _contactService: ContactService,
    private _businessClockService: BusinessClockService,
  ) {
    this.todayDate = this._businessClockService.nowUtc();
  }

  ngOnInit() {
    this._initTaskForm();
    this._initRecurrenceForm();
    this._getTimeZones();
    this.mode = this.data.data.mode || 'open';
    this.action = this.data.data.action;

    this.allStatuses = [...this.tasksService.taskStatus];
    if (this.data?.data) {
      this._getUsersByTenantId();
    }
    if (!this.data?.data?.id) {
      this._filterStatus();
    } else {
      this._getTaskByCurrentId();
    }
    this._getFullName();
  }

  private _getTimeZones(): void {
    this.tasksService
      .getAllTimeZones()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (response) => {
          this.timeZones = response.map((item) => ({
            label: item.key,
            value: item.value,
          }));
        },
        error: () => {
          this.timeZones = TimeZones;
        },
      });
  }

  private _disableForm(form: FormGroup): void {
    form.disable();
  }

  private _initTaskForm(): void {
    this.taskForm = this._fb.group({
      ownerID: this._fb.control<string | null>(null),
      cmTaskSubject: this._fb.control<string>('', [
        Validators.required,
        Validators.maxLength(300),
      ]),
      scheduleType: this._fb.control<string>('dueDate'),
      recurrenceSetting: this._fb.control<string>(''),
      dateTimeSLA: this._fb.control<moment.Moment | null>(null),
      myCMTaskPriority: this._fb.control<TaskPriorityEnum>(
        TaskPriorityEnum.Low,
        [Validators.required],
      ),
      dateTimeCreated: this._fb.control<moment.Moment | null>(null),

      myCMTaskStatus: this._fb.control<TaskStatusEnumOld>(
        TaskStatusEnumOld.New,
      ),
      cmTaskFullDescription: this._fb.control<string>('', [
        Validators.maxLength(300),
      ]),
      cmTags: this._fb.control<string[]>([], Validators.maxLength(20)),
      cmTaskComments: this._fb.control<CommentaryItemInterface[]>([]),
      transforFromObjID: this._fb.control<string>(''),
    });

    if (this.data?.data?.transformData) {
      const { transformFrom, item } = this.data.data.transformData;
      switch (transformFrom) {
        case TransformFrom.Vulnerability:
          this.taskForm.get('cmTaskSubject')?.setValue(item.Name);
          const tags =
            typeof item.Tags === 'string' ? [item.Tags] : item.Tags || [];
          this.taskForm.get('cmTags')?.setValue(tags);
          this.taskForm.enable();
          break;
        case TransformFrom.Alert:
        case TransformFrom.Activity:
          this.taskForm.get('cmTaskSubject')?.setValue(item.name);
          this.taskForm
            .get('cmTaskFullDescription')
            ?.setValue(item.description);
          this.taskForm
            .get('cmTags')
            ?.setValue(item.cmTags.map((tag: any) => tag.tagName));
          this.taskForm.enable();
          break;
      }
    }
  }

  private _initRecurrenceForm(): void {
    this.recurrenceForm = this._fb.group({
      StartDateTime: this._fb.control<moment.Moment | null>(null),
      EndDateTime: this._fb.control<moment.Moment | null>(null),
      StartTime: this._fb.control<string | null>(null),
      StartTimeFormat: this._fb.control<string | null>('AM'),
      EndTimeFormat: this._fb.control<string | null>('AM'),
      EndTime: this._fb.control<string | null>(null),
      RecurrenceType: this._fb.control<string | null>(DailyRecurrence, [
        Validators.required,
      ]),
      Interval: this._fb.control<string | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(60),
      ]),
      DaysOfWeek: this._fb.control<string[] | null>(null),
      TimeZoneName: this._fb.control<string | null>(null, [
        Validators.required,
      ]),
      DaysOfYearIndex: this._fb.control<number | null>(null, [
        Validators.min(1),
        Validators.max(364),
      ]),
      DaysOfMonthIndex: this._fb.control<number | null>(null, [
        Validators.min(1),
        Validators.max(30),
      ]),
    });
  }

  private _initFormSubs(): void {
    this.f['ownerID'].valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this._destroying$))
      .subscribe((value) => {
        if (!value) this.f['myCMTaskStatus'].setValue(TaskStatusEnumOld.New);
        else {
          if (this.f['myCMTaskStatus'].value < TaskStatusEnumOld.Assigned)
            this.f['myCMTaskStatus'].setValue(TaskStatusEnumOld.Assigned);
        }
      });

    this.f['scheduleType'].valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this._destroying$))
      .subscribe((value) => {
        if (value === 'recurrence') {
          this.f['dateTimeSLA'].setValue(null);
        }
      });
  }

  private _initRecurrenceFormSub(): void {
    this.recurrenceForm
      .get('Interval')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroying$))
      .subscribe((interval) => {
        if (parseInt(interval) === 1) {
          const allDays = Object.values(
            this.weekDays.map((item) => item.value),
          );
          this.r['RecurrenceType'].setValue(DailyRecurrence);
          this.r['DaysOfWeek'].setValue(allDays);
        } else {
          this.r['RecurrenceType'].setValue(WeeklyRecurrence);
          this.r['DaysOfWeek'].setValue(null);
        }
      });

    this.recurrenceForm
      .get('RecurrenceType')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroying$))
      .subscribe((item) => {
        switch (item) {
          case 'MonthlyRecurrence':
            this.textOfRecurrenceType = ' month';
            break;
          case 'YearlyRecurrence':
            this.textOfRecurrenceType = ' year';
            break;
          default:
            this.textOfRecurrenceType = this.defaultTextRecurrenceType;
            break;
        }
      });
  }

  private _getUsersByTenantId(): void {
    this.tasksService.loading = true;

    this.taskForm.disable();

    this._userService
      .getRecipientUserList()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (response) => {
          if (response && response.length) {
            this.usersByTenantId = response;
          }

          this.tasksService.loading = false;
          this.taskForm.enable();
        },
        error: (err) => {
          //TODO ADD TOASTR ERROR
          this.tasksService.loading = false;
          this.taskForm.enable();
        },
        complete: () => {
          !this.data?.isEditable && this.taskForm.disable();
        },
      });
  }

  private _getTaskByCurrentId() {
    this.taskForm.disable();
    this.tasksService.loading = true;
    this.tasksService.getTaskById(this.data.data.id).subscribe({
      next: (response) => {
        this.data.data = response.data.data;
        this._transformCommentaries();
        this.taskForm.enable();
        this.tasksService.loading = false;
        this._patchValue();
        this._initFormSubs();
        this._initRecurrenceFormSub();
      },
      complete: () => {
        this.tasksService.loading = false;
      },
    });
  }

  private _transformCommentaries() {
    if (this.data?.data?.cmTaskComments?.length) {
      this.comments = JSON.parse(this.data?.data?.cmTaskComments);
    }
  }

  private _patchValue(): void {
    if (this.data?.data) {
      const dataToPatch = cloneData<TaskFormInterface>(this.data.data);

      if (dataToPatch.dateTimeSLA) {
        dataToPatch.scheduleType = 'dueDate';
      }
      if ((dataToPatch.recurrenceSetting as string)?.length) {
        dataToPatch.scheduleType = 'recurrence';
      }

      this._filterStatus(dataToPatch.myCMTaskStatus as any);

      if ((dataToPatch.recurrenceSetting as string)?.length) {
        this._patchValueToRecurrence(dataToPatch.recurrenceSetting as string);
      }

      if (dataToPatch.dateTimeSLA === '0001-01-01T00:00:00')
        dataToPatch.dateTimeSLA = null;

      dataToPatch.cmTags = dataToPatch.cmTags
        ? (dataToPatch.cmTags as string).split(',')
        : [];

      dataToPatch.cmTaskComments = !this.comments ? [] : this.comments;
      this.taskForm.patchValue({
        ...dataToPatch,
      });
    }

    this._validateInputs();
  }

  private _filterStatus(status: string | number = ''): void {
    const statuses = Object.values(this.tasksService.taskStatus);
    const currentStatus = statuses.find((item) => item.value === status);
    if (+status > 0) {
      this.allStatuses = statuses.slice(1);
    }
    if (!status || (currentStatus && currentStatus?.value < 1)) {
      this.allStatuses = statuses.slice(0, 2);
    }
  }

  private _patchValueToRecurrence(values: string): void {
    const valuesObject = JSON.parse(values);
    const { cmReccurenceSetting } = valuesObject;
    const { RecurrencePattern, StartDateTime, EndDateTime, TimeZoneName } =
      cmReccurenceSetting;
    const defaultValue: any = {};
    if (StartDateTime) {
      const {
        date: startDateTime,
        time: startTime,
        format: startTimeFormat,
      }: any = this._convertDataToInsertInForm(StartDateTime as string);
      defaultValue.StartDateTime = moment(startDateTime);
      defaultValue.StartTime = startTime;
      defaultValue.StartTimeFormat = startTimeFormat;
    }
    if (EndDateTime) {
      const {
        date: endDateTime,
        time: endTime,
        format: endTimeFormat,
      }: any = this._convertDataToInsertInForm(EndDateTime as string);

      defaultValue.EndDateTime = moment(endDateTime);
      defaultValue.EndTime = endTime;
      defaultValue.EndTimeFormat = endTimeFormat;
    }
    const patchedValue = {
      ...defaultValue,
      ...RecurrencePattern,
      TimeZoneName,
    };
    this.recurrenceForm.patchValue(patchedValue);
  }

  private _convertDataToInsertInForm(datetime: string): any {
    const actualDatetime = moment(datetime)
      .utc(true)
      .format('YYYY-MM-DD hh:mm A');
    const [date, time, format] = actualDatetime.split(' ');
    return { date, time, format };
  }

  private _convertToUTCDatetime(
    date: any,
    time: string,
    format: string,
  ): string {
    if (time && format && date) {
      const timeObj = moment(`${time} ${format}`, 'h:mm A').utc();
      const combinedDatetime = date?.clone()?.set({
        hour: timeObj.hour(),
        minute: timeObj.minute(),
      });
      return combinedDatetime.utc(true).toJSON();
    }
    return date?.utc(true)?.toJSON();
  }

  private _transformValues(): RecurrenceSettingInterface {
    const unnecesaryFields = [
      'StartTime',
      'StartTimeFormat',
      'EndTime',
      'EndTimeFormat',
    ];
    const recurrenceData: RecurrenceSettingInterface =
      this.recurrenceForm.getRawValue();

    const startDateTime =
      typeof recurrenceData.StartDateTime === 'string'
        ? moment(recurrenceData.StartDateTime)
        : recurrenceData.StartDateTime;
    const endDateTime =
      typeof recurrenceData.EndDateTime === 'string'
        ? moment(recurrenceData.EndDateTime)
        : recurrenceData.EndDateTime;
    const startDate = this._convertToUTCDatetime(
      startDateTime,
      recurrenceData.StartTime as string,
      recurrenceData.StartTimeFormat as string,
    );
    const endDate = this._convertToUTCDatetime(
      endDateTime,
      recurrenceData.EndTime as string,
      recurrenceData.EndTimeFormat as string,
    );

    recurrenceData.StartDateTime = startDate;
    recurrenceData.EndDateTime = endDate;

    return removeUnnecesaryKeysFromObject(
      unnecesaryFields,
      recurrenceData,
    ) as RecurrenceSettingInterface;
  }

  private _transformRecurrencecToPattern(data: RecurrenceSettingInterface) {
    const requestData = {
      cmReccurenceSetting: {
        StartDateTime: data.StartDateTime,
        EndDateTime: data.EndDateTime,
        TimeZoneName: data.TimeZoneName,
        RecurrencePattern: {
          RecurrenceType: data.RecurrenceType,
          DaysOfWeek: data.DaysOfWeek,
          Interval: data.Interval,
          DaysOfMonthIndex: data.DaysOfMonthIndex,
          DaysOfYearIndex: data.DaysOfYearIndex,
        },
      },
    };

    return requestData;
  }

  public onSubmit(): void {
    this.taskForm.markAllAsTouched();

    if (this.f['scheduleType'].value === 'recurrence') {
      this.recurrenceForm.markAllAsTouched();
      if (this.recurrenceForm.invalid) return;
    }

    if (this.taskForm.invalid) return;

    const recurrenceData = this._transformValues();
    const transformedRecurrenceData =
      this._transformRecurrencecToPattern(recurrenceData);
    const requestData: TaskFormInterface = this.taskForm.getRawValue();

    if (this.f['scheduleType'].value === 'recurrence') {
      requestData.recurrenceSetting = JSON.stringify(transformedRecurrenceData);
    } else {
      requestData.recurrenceSetting = null;
    }

    requestData.cmTags = (requestData.cmTags as string[]).join(',');

    if (requestData.dateTimeSLA) {
      const dueDate: string = moment(requestData.dateTimeSLA)
        .utc(true)
        .toJSON();

      requestData.dateTimeSLA = dueDate;
    }
    delete requestData.scheduleType;

    this.tasksService.loading = true;
    this.taskForm.disable();

    if (this.action === 'create' || this.data?.data?.transformData) {
      delete requestData.myCMTaskStatus;

      if (this.data?.data?.transformData) {
        const { transformFrom, item } = this.data.data.transformData;
        switch (transformFrom) {
          case TransformFrom.Vulnerability:
            requestData.transforFromObjID = item.VulnerabilityID;
            break;
          case TransformFrom.Alert:
          case TransformFrom.Activity:
            requestData.transforFromObjID = item.id.toString();
            break;
        }

        requestData.CMTaskTransformFrom = transformFrom;
      }

      this._createTaskMethod(requestData);
    } else {
      requestData.id = this.data.data.id;
      this._updateTaskMethod(requestData);
    }
  }

  private _createTaskMethod(requestData: TaskFormInterface): void {
    this.tasksService
      .createNewTask(requestData)
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (response) => {
          if (response?.data) {
            this._toastr.success(response.data);
            this.dialogRef.close(true);
          } else {
            this._toastr.error(response.errors[0]);
          }

          this.tasksService.loading = false;
          this.taskForm.enable();
        },
        error: (err) => {
          //TODO ADD TOASTR ERROR
          this.tasksService.loading = false;
          this.taskForm.enable();
        },
      });
  }

  private _updateTaskMethod(requestData: TaskFormInterface): void {
    this.tasksService
      .updateTaskById(requestData)
      .pipe(
        switchMap((response) => {
          if (response?.data) {
            this._toastr.success(response.data);
            this.dialogRef.close(true);
            // this._taskComment && this.addNewComment(this._taskComment);
            return of(true);
          } else {
            this._toastr.error(response.errors[0]);
            return of(null);
          }
        }),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: (response) => {
          this.tasksService.loading = false;
          this.taskForm.enable();
        },
        error: (err) => {
          //TODO ADD TOASTR ERROR
          this.tasksService.loading = false;
          this.taskForm.enable();
        },
      });
  }

  public onCancel(): void {
    const formData = this.taskForm.getRawValue();
    formData.cmTags = formData.cmTags.join(',');

    if (Object.values(formData).every((value) => !value)) {
      this.dialogRef.close(false);
      return;
    }

    const dialogConfig: DialogConfigInterface = {
      title: `${this._translate.instant('DIALOG.TITLE.DISCARD_DRAFT')}`,
      isCloseBtn: false,
      disableClose: true,
      successBtn: 'BUTTON.KEEP_WORKING',
      cancelBtn: 'BUTTON.DISCARD_DRAFT',
    };

    this._dialog
      .open(ConfirmDialogComponent, {
        data: dialogConfig,
        width: EDialogWidth.MEDIUM,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === false) this.dialogRef.close(result);
      });
  }

  public addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) this.f['cmTags'].value.push(value);

    event.chipInput!.clear();
  }

  public removeTag(tag: string): void {
    const index = this.f['cmTags'].value.indexOf(tag);

    if (index >= 0) this.f['cmTags'].value.splice(index, 1);
  }

  public editTag(tag: string, event: MatChipEditedEvent): void {
    const value = (event.value || '').trim();

    if (!value) {
      this.removeTag(tag);
      return;
    }

    const index = this.f['cmTags'].value.indexOf(tag);
    if (index >= 0) {
      this.f['cmTags'].value[index] = value;
    }
  }

  public get f(): { [key: string]: AbstractControl } {
    return this.taskForm.controls;
  }

  public get r(): { [key: string]: AbstractControl } {
    return this.recurrenceForm.controls;
  }

  public addNewComment(message: string): void {
    const newMessage = {
      Comment: message,
      DateTimeComment: moment().utc(true).toJSON(),
      UserId: localStorage.getItem('userId'),
      UserFullName: `${this._currentUser?.name} ${this._currentUser?.lName}`,
    };
    this.f['cmTaskComments'].patchValue([
      ...this.f['cmTaskComments'].value,
      newMessage,
    ]);
  }

  private _validateInputs() {
    if (this.mode === 'open') {
      this._disableForm(this.taskForm);
      this._disableForm(this.recurrenceForm);
    } else {
      this.taskForm.enable();
      this.recurrenceForm.enable();
    }

    if (this.data.isTransform) {
      this.taskForm
        .get('cmTaskFullDescription')
        ?.setValue(this.data.data.description);
    }

    if (!this.data?.data) {
      this.taskForm.get('myCMTaskStatus')?.disable();
    }
  }

  private _getFullName(): void {
    this._contactService
      .getContact()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (response: any) => {
          this._currentUser = response;
        },
      });
  }

  ngOnDestroy(): void {
    this.taskForm.reset();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
